import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Toolbar,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  CalendarMonth,
  Upload,
  Download,
  Delete,
  SwapHoriz,
  Work,
  ToggleOn,
  Close,
  FileDownload,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Employee, EmploymentStatus, displayName } from '../../types';
import { apiService } from '../../services/api';
import { apiFetch } from '../../services/apiFetch';
import AddEmployeeModal from '../../components/AddEmployeeModal';
import EditEmployeeModal from '../../components/EditEmployeeModal';

const CSV_TEMPLATE_HEADERS = 'firstName,lastName,email,phone,entityName,departmentName,positionTitle,employmentStatus,startDate';
const CSV_TEMPLATE_EXAMPLE = 'Jane,Doe,jane.doe@example.com,+85291234567,My Company Ltd,Engineering,Software Engineer,active,2024-01-15';

interface Department { id: string; name: string; }
interface Position { id: string; title: string; }

const Employees: React.FC = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0 = Active, 1 = Archive

  // Pagination (client-side over the filtered list)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Bulk action dialogs
  const [bulkDeptOpen, setBulkDeptOpen] = useState(false);
  const [bulkPositionOpen, setBulkPositionOpen] = useState(false);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkDeactivateOpen, setBulkDeactivateOpen] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [bulkDeptId, setBulkDeptId] = useState('');
  const [bulkPositionId, setBulkPositionId] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  // CSV import state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; failed: number; errors: { row: number; error: string }[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      // Request the paginated endpoint (capped page size). The client-side
      // search + active/archive tabs still operate over the returned rows, so
      // we request the max page size the server allows. getEmployeesPaged
      // normalises both the paginated and legacy plain-array responses.
      const { data } = await apiService.getEmployeesPaged({ page: 1, pageSize: 200 });
      setEmployees(data);
    } catch (err: any) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDepts = useCallback(async () => {
    try {
      const data = await apiService.getDepartments();
      setDepartments(data);
    } catch { /* ignore */ }
  }, []);

  const fetchPositions = useCallback(async () => {
    try {
      const data = await apiService.getPositions();
      setPositions(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchEmployees();
    fetchDepts();
    fetchPositions();
  }, [fetchEmployees, fetchDepts, fetchPositions]);

  const handleEmployeeAdded = () => fetchEmployees();
  const handleEmployeeUpdated = () => fetchEmployees();

  const handleEditEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedEmployee(null);
  };

  const getStatusColor = (status: EmploymentStatus) => {
    switch (status) {
      case EmploymentStatus.ACTIVE: return 'success';
      case EmploymentStatus.TERMINATED: return 'error';
      case EmploymentStatus.ON_LEAVE: return 'warning';
      default: return 'default';
    }
  };

  const handleDownloadTemplate = () => {
    const csv = `${CSV_TEMPLATE_HEADERS}\n${CSV_TEMPLATE_EXAMPLE}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await apiService.importEmployees(importFile);
      setImportResult(result);
      if (result.imported > 0) fetchEmployees();
    } catch (err: any) {
      setImportResult({ imported: 0, failed: 0, errors: [{ row: 0, error: err.message }] });
    } finally {
      setImporting(false);
    }
  };

  const handleCloseImport = () => {
    setIsImportOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  const activeEmployees = employees.filter(e => e.employmentStatus !== EmploymentStatus.TERMINATED);
  const archivedEmployees = employees.filter(e => e.employmentStatus === EmploymentStatus.TERMINATED);
  const currentList = activeTab === 0 ? activeEmployees : archivedEmployees;

  const filteredEmployees = currentList.filter(
    (employee) =>
      employee.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.nickname && employee.nickname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.department?.name.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  );

  // Reset to first page whenever the filter inputs change so the user is never
  // stranded on an out-of-range page.
  useEffect(() => {
    setPage(0);
  }, [searchTerm, activeTab, rowsPerPage]);

  const pagedEmployees = filteredEmployees.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // ── Bulk selection helpers ──────────────────────────────────────────────────

  const allFilteredSelected = filteredEmployees.length > 0 &&
    filteredEmployees.every((e) => selectedIds.has(e.id));
  const someFilteredSelected = filteredEmployees.some((e) => selectedIds.has(e.id)) && !allFilteredSelected;

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEmployees.map((e) => e.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // ── Export CSV ──────────────────────────────────────────────────────────────

  const handleExport = async (idsToExport?: string[]) => {
    const ids = idsToExport ?? (selectedIds.size > 0 ? Array.from(selectedIds) : filteredEmployees.map((e) => e.id));
    try {
      const res = await apiFetch('/api/employees/bulk-export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeIds: ids }),
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employees-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showSnack(`Exported ${ids.length} employee${ids.length !== 1 ? 's' : ''}`);
    } catch (err: any) {
      showSnack('Export failed: ' + (err?.message || 'Unknown error'), 'error');
    }
  };

  // ── Bulk Transfer Department ────────────────────────────────────────────────

  const handleBulkTransferDept = async () => {
    if (!bulkDeptId) return;
    setBulkLoading(true);
    try {
      const res = await apiFetch('/api/employees/bulk-update', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeIds: Array.from(selectedIds), updates: { departmentId: bulkDeptId } }),
      });
      const data = await res.json();
      showSnack(`Updated ${data.updated} employee${data.updated !== 1 ? 's' : ''}`);
      setBulkDeptOpen(false);
      setBulkDeptId('');
      clearSelection();
      fetchEmployees();
    } catch (err: any) {
      showSnack('Transfer failed: ' + (err?.message || 'Unknown error'), 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Bulk Change Position ────────────────────────────────────────────────────

  const handleBulkChangePosition = async () => {
    if (!bulkPositionId) return;
    setBulkLoading(true);
    try {
      const res = await apiFetch('/api/employees/bulk-update', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeIds: Array.from(selectedIds), updates: { positionId: bulkPositionId } }),
      });
      const data = await res.json();
      showSnack(`Updated ${data.updated} employee${data.updated !== 1 ? 's' : ''}`);
      setBulkPositionOpen(false);
      setBulkPositionId('');
      clearSelection();
      fetchEmployees();
    } catch (err: any) {
      showSnack('Update failed: ' + (err?.message || 'Unknown error'), 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Bulk Update Status ──────────────────────────────────────────────────────

  const handleBulkUpdateStatus = async () => {
    if (!bulkStatus) return;
    setBulkLoading(true);
    try {
      const res = await apiFetch('/api/employees/bulk-update', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeIds: Array.from(selectedIds), updates: { employmentStatus: bulkStatus } }),
      });
      const data = await res.json();
      showSnack(`Updated ${data.updated} employee${data.updated !== 1 ? 's' : ''}`);
      setBulkStatusOpen(false);
      setBulkStatus('');
      clearSelection();
      fetchEmployees();
    } catch (err: any) {
      showSnack('Update failed: ' + (err?.message || 'Unknown error'), 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Bulk Deactivate ─────────────────────────────────────────────────────────

  const handleBulkDeactivate = async () => {
    setBulkLoading(true);
    try {
      await apiFetch('/api/employees/bulk-deactivate', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeIds: Array.from(selectedIds) }),
      });
      showSnack(`Deactivated ${selectedIds.size} employee${selectedIds.size !== 1 ? 's' : ''}`);
      setBulkDeactivateOpen(false);
      clearSelection();
      fetchEmployees();
    } catch (err: any) {
      showSnack('Deactivate failed: ' + (err?.message || 'Unknown error'), 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  const selCount = selectedIds.size;

  return (
    <Box>
      <Box display="flex" justifyContent={{ xs: 'column', sm: 'space-between' }} alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} mb={3}>
        <Typography variant="h4">Employees</Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button variant="outlined" startIcon={<CalendarMonth />} onClick={() => navigate('/employees/leave')}>
            Manage Leave
          </Button>
          <Button variant="outlined" startIcon={<Upload />} onClick={() => setIsImportOpen(true)}>
            Import CSV
          </Button>
          <Tooltip title="Export all filtered employees to CSV">
            <Button variant="outlined" startIcon={<FileDownload />} onClick={() => handleExport(filteredEmployees.map((e) => e.id))}>
              Export CSV
            </Button>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setIsAddModalOpen(true)}>
            Add Employee
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            placeholder={activeTab === 0 ? "Search active employees..." : "Search archived employees..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
          />
        </Grid>
      </Grid>

      {/* Bulk action toolbar — appears when any employees are selected */}
      {selCount > 0 && (
        <Paper
          elevation={3}
          sx={{ mb: 2, bgcolor: 'warning.light', borderRadius: 1 }}
        >
          <Toolbar sx={{ gap: 1, flexWrap: 'wrap', minHeight: '56px !important', py: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
              {selCount} employee{selCount !== 1 ? 's' : ''} selected
            </Typography>
            <Button size="small" variant="outlined" startIcon={<SwapHoriz />} onClick={() => setBulkDeptOpen(true)}>
              Transfer Dept
            </Button>
            <Button size="small" variant="outlined" startIcon={<Work />} onClick={() => setBulkPositionOpen(true)}>
              Change Position
            </Button>
            <Button size="small" variant="outlined" startIcon={<ToggleOn />} onClick={() => setBulkStatusOpen(true)}>
              Update Status
            </Button>
            <Button size="small" variant="outlined" startIcon={<Download />} onClick={() => handleExport()}>
              Export Selected
            </Button>
            <Button size="small" variant="outlined" color="error" startIcon={<Delete />} onClick={() => setBulkDeactivateOpen(true)}>
              Deactivate
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton size="small" onClick={clearSelection} title="Clear selection">
              <Close />
            </IconButton>
          </Toolbar>
        </Paper>
      )}

      <Paper sx={{ overflowX: 'auto' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someFilteredSelected}
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </TableCell>
                <TableCell>Employee #</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Manager</TableCell>
                <TableCell>Base Location</TableCell>
                <TableCell>Monthly Salary</TableCell>
                <TableCell>Access</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedEmployees.map((employee) => (
                <TableRow
                  key={employee.id}
                  hover
                  selected={selectedIds.has(employee.id)}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/employees/${employee.id}`)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(employee.id)}
                      onChange={() => toggleSelect(employee.id)}
                    />
                  </TableCell>
                  <TableCell>{employee.employeeNumber || '-'}</TableCell>
                  <TableCell>{displayName(employee)}</TableCell>
                  <TableCell>{employee.email}</TableCell>
                  <TableCell>{employee.department?.name || '-'}</TableCell>
                  <TableCell>{employee.position?.title || '-'}</TableCell>
                  <TableCell>{employee.manager ? displayName(employee.manager) : '-'}</TableCell>
                  <TableCell>{employee.country || '-'}</TableCell>
                  <TableCell>
                    {employee.monthlySalary
                      ? `${employee.salaryCurrency || 'HKD'} ${Number(employee.monthlySalary).toLocaleString()}`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {employee.access ? (
                      <Chip label="Approver" color="primary" size="small" />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={employee.employmentStatus.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(employee.employmentStatus)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {employee.startDate ? new Date(employee.startDate).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleEditEmployee(employee); }}
                      title="Edit Employee Profile"
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredEmployees.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography color="text.secondary">
                      {searchTerm ? 'No employees found matching your search.' : 'No employees found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredEmployees.length}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          Total: {filteredEmployees.length} employees
          {searchTerm && ` (filtered from ${employees.length})`}
          {selCount > 0 && ` · ${selCount} selected`}
        </Typography>
      </Box>

      {/* ── Bulk Transfer Department Dialog ─────────────────────────────── */}
      <Dialog open={bulkDeptOpen} onClose={() => setBulkDeptOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Department — {selCount} employee{selCount !== 1 ? 's' : ''}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>New Department</InputLabel>
            <Select value={bulkDeptId} onChange={(e) => setBulkDeptId(e.target.value)} label="New Department">
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeptOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkTransferDept} disabled={!bulkDeptId || bulkLoading}
            startIcon={bulkLoading ? <CircularProgress size={16} /> : <SwapHoriz />}>
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Change Position Dialog ──────────────────────────────────── */}
      <Dialog open={bulkPositionOpen} onClose={() => setBulkPositionOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Position — {selCount} employee{selCount !== 1 ? 's' : ''}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>New Position</InputLabel>
            <Select value={bulkPositionId} onChange={(e) => setBulkPositionId(e.target.value)} label="New Position">
              {positions.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkPositionOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkChangePosition} disabled={!bulkPositionId || bulkLoading}
            startIcon={bulkLoading ? <CircularProgress size={16} /> : <Work />}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Update Status Dialog ────────────────────────────────────── */}
      <Dialog open={bulkStatusOpen} onClose={() => setBulkStatusOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Status — {selCount} employee{selCount !== 1 ? 's' : ''}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>New Status</InputLabel>
            <Select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} label="New Status">
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="on_leave">On Leave</MenuItem>
              <MenuItem value="terminated">Terminated</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkStatusOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkUpdateStatus} disabled={!bulkStatus || bulkLoading}
            startIcon={bulkLoading ? <CircularProgress size={16} /> : <ToggleOn />}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Deactivate Confirm Dialog ───────────────────────────────── */}
      <Dialog open={bulkDeactivateOpen} onClose={() => setBulkDeactivateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Deactivate {selCount} Employee{selCount !== 1 ? 's' : ''}?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This will set the employment status of {selCount} employee{selCount !== 1 ? 's' : ''} to <strong>Terminated</strong>.
            Employee records will be retained but they will no longer be active.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeactivateOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleBulkDeactivate} disabled={bulkLoading}
            startIcon={bulkLoading ? <CircularProgress size={16} /> : <Delete />}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CSV Import Dialog ────────────────────────────────────────────── */}
      <Dialog open={isImportOpen} onClose={handleCloseImport} maxWidth="sm" fullWidth>
        <DialogTitle>Import Employees via CSV</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Button variant="outlined" startIcon={<Download />} onClick={handleDownloadTemplate} size="small">
              Download CSV Template
            </Button>
            <Typography variant="caption" display="block" mt={1} color="text.secondary">
              Required columns: firstName, lastName, email, entityName. Optional: phone, departmentName, positionTitle, employmentStatus, startDate.
            </Typography>
          </Box>

          <Box
            border="2px dashed"
            borderColor="divider"
            borderRadius={1}
            p={3}
            textAlign="center"
            sx={{ cursor: 'pointer' }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
            <Upload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography>
              {importFile ? importFile.name : 'Click to select a CSV file'}
            </Typography>
          </Box>

          {importResult && (
            <Box mt={2}>
              {importResult.imported > 0 && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  ✅ Successfully imported {importResult.imported} employee{importResult.imported !== 1 ? 's' : ''}.
                </Alert>
              )}
              {importResult.failed > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  ⚠️ {importResult.failed} row{importResult.failed !== 1 ? 's' : ''} failed to import.
                  <List dense>
                    {importResult.errors.map((e, i) => (
                      <ListItem key={i} sx={{ py: 0 }}>
                        <ListItemText primary={`Row ${e.row}: ${e.error}`} />
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImport}>Close</Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!importFile || importing}
            startIcon={importing ? <CircularProgress size={16} /> : <Upload />}
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Success / Error Snackbar ─────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <AddEmployeeModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onEmployeeAdded={handleEmployeeAdded}
      />

      <EditEmployeeModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        onEmployeeUpdated={handleEmployeeUpdated}
        employee={selectedEmployee}
      />
    </Box>
  );
};

export default Employees;
